import React, { useState, useRef, useEffect, useCallback, useMemo } from "react";
import "./GlobalDropdown.css";
import { useConstants } from "../../constant/constantProvider";
import dropdownIcon from "../../assets/images/dropdown-icon.svg";
import SearchBar from "../SearchBar/SearchBar";
import Tooltip from "../Tooltip/Tooltip";

const Dropdown = ({
  options = [],
  value,
  onChange,
  placeholder = "Select",
  isDisabled = false,
  type = "single",
  enableSearchBar = false,
  isLoading = false,
  required = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const containerRef = useRef(null);
  const searchRef = useRef(null);

  const { dropdown: t = {} } = useConstants();

  const isMulti = type === "multi";
  const selected = useMemo(
    () => (isMulti ? (Array.isArray(value) ? value : []) : value),
    [isMulti, value]
  );

  // Normalise options → [{ label, value }]
  const normalised = options.map((opt) =>
    typeof opt === "object" && opt !== null ? opt : { label: opt, value: opt }
  );

  // Filtered by search
  const filtered = search
    ? normalised.filter((o) =>
      o.label.toString().toLowerCase().includes(search.toLowerCase())
    )
    : normalised;

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
        setSearch("");
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Focus search when opening
  useEffect(() => {
    if (isOpen && enableSearchBar && !isLoading && !isDisabled && searchRef.current) {
      setTimeout(() => searchRef.current?.focus(), 50);
    }
  }, [isOpen, enableSearchBar, isLoading, isDisabled]);

  const toggle = () => {
    if (isDisabled) return;
    setIsOpen((prev) => {
      if (prev) setSearch("");
      return !prev;
    });
  };

  const handleOptionClick = useCallback(
    (opt) => {
      if (isMulti) {
        const exists = selected.some((v) => v === opt.value);
        const next = exists
          ? selected.filter((v) => v !== opt.value)
          : [...selected, opt.value];
        onChange?.(next);
      } else {
        onChange?.(opt.value);
        setIsOpen(false);
        setSearch("");
      }
    },
    [isMulti, selected, onChange]
  );

  const hasValue = isMulti
    ? selected.length > 0
    : selected !== null && selected !== undefined && selected !== "";

  const getTriggerLabel = () => {
    if (!hasValue) return null;
    if (isMulti) {
      if (selected.length === 1) {
        const found = normalised.find((o) => o.value === selected[0]);
        return found ? found.label : selected[0];
      }
      return `${selected.length} ${t.SELECTED}`;
    }
    const found = normalised.find((o) => o.value === selected);
    return found ? found.label : selected;
  };

  const isOptionSelected = (optValue) =>
    isMulti ? selected.includes(optValue) : selected === optValue;

  return (
    <div
      ref={containerRef}
      className={[
        "dd-root",
        isOpen ? "dd-open" : "",
        isDisabled ? "dd-disabled" : "",
        hasValue ? "dd-has-value" : "",
      ].join(" ").trim()}
    >
      {/* ── Trigger ───────────────────────────────────── */}
      <div
        className="dd-trigger"
        onClick={toggle}
        role="combobox"
        aria-controls="dd-options-list"
        aria-expanded={isOpen}
        aria-disabled={isDisabled}
        tabIndex={isDisabled ? -1 : 0}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") toggle(); }}
      >
        {/* Floating label */}
        <span className="dd-label">
          {placeholder}
          {required && <span className="dd-required"> *</span>}
        </span>

        {/* Selected value tooltip */}
        {hasValue && (
          <Tooltip
            tooltipText={getTriggerLabel()}
            tooltipPosition="bottom"
            className="dd-value-tooltip"
          >
            <span className="dd-value">{getTriggerLabel()}</span>
          </Tooltip>
        )}

        {/* Right controls */}
        <div className="dd-controls">
          {/* Dropdown icon from assets */}
          <span className={`dd-chevron ${isOpen ? "dd-chevron-up" : ""}`}>
            <img
              src={dropdownIcon}
              alt=""
              className="dd-chevron-icon"
              aria-hidden="true"
            />
          </span>
        </div>
      </div>

      {/* ── Dropdown panel ────────────────────────────── */}
      {isOpen && (
        <div className="dd-panel" role="listbox" aria-multiselectable={isMulti}>
          {/* Reusable SearchBar component */}
          {enableSearchBar && (
            <SearchBar
              ref={searchRef}
              value={search}
              onChange={setSearch}
              placeholder={t.SEARCH_PLACEHOLDER}
              disabled={isLoading || isDisabled}
            />
          )}

          {/* Options */}
          <ul id="dd-options-list" className="dd-options">
            {isLoading ? (
              <li className="dd-option dd-loading">{t.LOADING}</li>
            ) : filtered.length > 0 ? (
              filtered.map((opt) => {
                const sel = isOptionSelected(opt.value);
                return (
                  <li
                    key={opt.value}
                    className={`dd-option ${sel ? "dd-option-selected" : ""}`}
                    onClick={() => handleOptionClick(opt)}
                    role="option"
                    aria-selected={sel}
                  >
                    {isMulti && (
                      <span className={`dd-checkbox ${sel ? "dd-checkbox-checked" : ""}`}>
                        {sel && (
                          <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                            <path d="M1.5 5L4 7.5L8.5 2.5" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        )}
                      </span>
                    )}
                    <span className="dd-option-label">{opt.label}</span>
                  </li>
                );
              })
            ) : (
              <li className="dd-option dd-no-options">{t.NO_OPTIONS}</li>
            )}
          </ul>

          {/* Multi footer */}
          {isMulti && selected.length > 0 && (
            <div className="dd-multi-footer">
              <span className="dd-multi-count">{selected.length} {t.SELECTED}</span>
              <button
                className="dd-done-btn"
                onClick={() => { setIsOpen(false); setSearch(""); }}
              >
                {t.DONE}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Dropdown;
